import type { QueryClient } from '@tanstack/react-query';

/**
 * Tras una acción, el listado visible y el Inicio tienen que leer de nuevo.
 * El cliente guarda las consultas 30s y, por defecto, solo refresca la pantalla
 * montada. El Inicio se quedaba con el número anterior hasta recargar.
 */
export type StoreRefreshAction =
    | 'orders'
    | 'product-batch'
    | 'product-save'
    | 'fichas-import'
    | 'supplier-sync'
    | 'supplier-save'
    | 'brand-assign'
    | 'brand-save'
    | 'merch-stock';

const HOME = ['admin', 'store-home'] as const;
const ORDERS = ['admin', 'store-orders'] as const;
const PRODUCTS = ['admin', 'store-products'] as const;
const CATALOG_PRODUCTS = ['admin', 'store-catalog-products'] as const;
const CATALOG_DETAIL = ['admin', 'store-catalog-product'] as const;
const FICHAS_STATS = ['admin', 'store-catalog-fichas-stats'] as const;
const PROPIOS = ['admin', 'store-propios'] as const;
const SUPPLIERS = ['admin', 'store-catalog-suppliers'] as const;
const SUPPLIER_ACTIVITY = ['admin', 'store-supplier-activity'] as const;
const SYNC_RUNS = ['admin', 'store-catalog-sync-runs'] as const;
const BRANDS = ['admin', 'store-brands'] as const;
const BRANDS_PENDING = ['admin', 'store-brands-pending'] as const;
const MERCH_STOCK = ['admin', 'merch-stock'] as const;
const MERCH_SUMMARY = ['admin', 'store-merch-summary'] as const;

export function storeSurfacesAfter(action: StoreRefreshAction): readonly (readonly string[])[] {
    switch (action) {
        case 'orders':
            return [ORDERS, HOME, MERCH_STOCK, MERCH_SUMMARY];
        case 'product-batch':
            return [PRODUCTS, CATALOG_PRODUCTS, FICHAS_STATS, PROPIOS, HOME, MERCH_SUMMARY];
        case 'product-save':
            return [PRODUCTS, CATALOG_PRODUCTS, CATALOG_DETAIL, FICHAS_STATS, SUPPLIERS, HOME];
        case 'fichas-import':
            return [PRODUCTS, CATALOG_PRODUCTS, CATALOG_DETAIL, FICHAS_STATS, HOME];
        case 'supplier-sync':
            return [SUPPLIERS, PRODUCTS, CATALOG_PRODUCTS, SYNC_RUNS, HOME];
        case 'supplier-save':
            return [SUPPLIERS, SUPPLIER_ACTIVITY, HOME];
        case 'brand-assign':
            return [BRANDS, BRANDS_PENDING, PRODUCTS, CATALOG_DETAIL, HOME];
        case 'brand-save':
            return [BRANDS, HOME];
        case 'merch-stock':
            return [MERCH_STOCK, PRODUCTS, MERCH_SUMMARY, HOME];
    }
}

export async function refreshStoreAfter(qc: QueryClient, action: StoreRefreshAction): Promise<void> {
    await Promise.all(
        storeSurfacesAfter(action).map((queryKey) =>
            qc.invalidateQueries({ queryKey: [...queryKey], refetchType: 'all' }),
        ),
    );
}
